<template>
  <responsive-drawer-layout>
    <template v-slot:drawer>
      <form-tree-slider
        class="mt-0"
        :groups="groupsSortedBySort"
        :usedGroupIds="usedGroupIds"
        root-text-key="formsRootHeader"
        childrenType="forms"
        @onFilterChange="(v) => (filterText = v)"
      >
        <template v-slot:content>
          <div v-for="{ group, formsInGroup } in formsByGroups" :key="group.id">
            <div v-if="group.name" class="text-h6 pa-3 item-bottom-border">{{ group.name }}</div>
            <base-list-item
              v-for="item in formsInGroup"
              :key="item.id"
              :file="item"
              :isSelected="displayedForm && item.id === displayedForm.id"
              :isUnreadCountFeatureEnabled="isUnreadCountFeatureEnabledByModule"
              :favoritesEnabled="false"
              :showPublishedDate="false"
              itemIcon="mdi-form-select"
              @onSelected="openSelectedForm(item)"
              readAtTextKey="formSent"
              notReadAtTextKey="formNotSent"
              :showMutationInfo="false"
            />
          </div>
        </template>
      </form-tree-slider>
    </template>
    <template v-slot:content v-if="dataLoaded">
      <form-builder
        v-if="displayedForm"
        :displayedForm="displayedForm"
        :formModel="formModel"
        :mediasModel="mediasModel"
        :dataLoaded="dataLoaded"
        :disableSendButton="disableSendButton"
        @formUpdated="formUpdated"
        @resetForm="resetForm"
        @sendForm="sendForm"
      />
      <empty-state-indicator v-else icon="mdi-text-box-search-outline" :text="$t('forms.noFormSelected')" />
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import FormBuilder from './FormBuilder';
import BaseListItem from '@/components/listItems/BaseListItem.vue';
import EmptyStateIndicator from '@/components/container/EmptyStateIndicator.vue';
import FormTreeSlider from '@/components/listItems/FormTreeSlider';
import dateUtils from '@/common/dateUtils';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import api from '@/api/formsApi';
import { preprocessImage } from '@/common/mediaUtils';
import uiHelpers from '@/common/uiHelpers';
//TODO: check if audit log entries must be created - https://confluence.innotix.com/display/oVPadNxG/Dokumentenmodul

export default {
  components: { ResponsiveDrawerLayout, FormBuilder, BaseListItem, EmptyStateIndicator, FormTreeSlider },
  mixins: [common, configMixin, dateTimeMixin],
  data() {
    return {
      formModel: {},
      mediasModel: [],
      disableSendButton: false,
      filterText: '',
      savedFormModel: {},
    };
  },
  mounted() {
    if (this.formId) {
      this.fillFormFromLocalStorage();
    }
    if (!this.displayedForm) {
      // open the drawer with the list of forms in case no form is selected
      // has no impact on device sizes where the drawer is always open
      this.mx_isLeftDrawerOpen = true;
    }

    this.$store.commit('forms/setSelectedGroups', { value: [] });
  },
  computed: {
    ...mapState({
      groups: (state) => state.forms.groups,
      formsById: (state) => state.forms.formsById,
      forms: (state) => state.forms.forms,
      autofill: (state) => state.forms.autofill,
      dataLoaded: (state) => state.forms.dataLoaded,
      selectedGroups: (state) => state.forms.selectedGroups,
    }),
    instance() {
      return Number(this.$route.params.instance);
    },
    instanceForms() {
      return this.forms.filter((f) => f.instance === this.instance);
    },
    isGroupFilterSelected() {
      return this.selectedGroups.length > 0;
    },
    usedGroupIds() {
      return this.instanceForms
        .filter((instance) => instance.groupIdFk)
        .map((instance) => instance.groupIdFk)
        .reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {});
    },
    formId() {
      return this.$route.params.formId ? this.$route.params.formId : undefined;
    },
    displayedForm() {
      return this.formId && this.formsById[this.formId];
    },
    formReports: {
      get() {
        return this.$store.state.forms.formReports;
      },
      set(value) {
        this.$store.state.forms.formReports = value;
      },
    },
    groupsSortedBySort() {
      return this.groups ? this.groups.slice().sort((a, b) => a.sort - b.sort) : [];
    },
    formsByGroups() {
      let groupsAndForms = [];

      let nonGroupedItems = this.instanceForms.filter((form) => !form.groupIdFk);

      if (this.filterText) {
        nonGroupedItems = nonGroupedItems.filter((instance) => instance.title.toLowerCase().includes(this.filterText.toLowerCase()));
      }

      if (!this.isGroupFilterSelected) groupsAndForms.push({ group: { id: -1 }, formsInGroup: nonGroupedItems });

      this.groupsSortedBySort.forEach((group) => {
        let formsInGroup = this.getFormsByGroup(group).filter((instance) => {
          if (this.isGroupFilterSelected) {
            return this.selectedGroups.includes(instance.groupIdFk);
          }
          return true;
        });

        if (this.filterText) {
          formsInGroup = formsInGroup.filter((instance) => instance.title.toLowerCase().includes(this.filterText.toLowerCase()));
        }

        if (formsInGroup.length > 0) {
          groupsAndForms.push({ group, formsInGroup });
        }
      });
      return groupsAndForms;
    },

    isUnreadCountFeatureEnabledByModule() {
      return this.mx_isUnreadCountFeatureEnabled(this.moduleType, this.instance);
    },
  },
  methods: {
    ...mapActions({
      sendFormAction: 'forms/sendForm',
    }),
    fillFormFromLocalStorage() {
      let forms = JSON.parse(localStorage.getItem('forms') || '{}');
      let thisForm = forms[this.formId];
      if (thisForm) {
        let { formModel, mediasModel } = thisForm;
        this.savedFormModel = formModel;
        this.mediasModel = mediasModel;
      }
    },
    getFormsByGroup(group) {
      let forms = this.instanceForms
        .filter((f) => f.groupIdFk === group.id)

        .sort((a, b) => a.sort - b.sort);
      return forms;
    },
    openSelectedForm(item) {
      this.mx_isLeftDrawerOpen = false;

      if (!this.displayedForm || this.displayedForm.id !== item.id) {
        uiHelpers.initiateDelayedMarkAsRead(this.$store.dispatch, item, 1500);
        this.$router.push(`/forms/${this.instance}/${item.id}`);
      }
    },
    formUpdated(key, value) {
      this.formModel = { ...this.formModel, [key]: value };
      let storedForms = JSON.parse(localStorage.getItem('forms') || '{}');
      storedForms[this.displayedForm.id] = { formModel: this.formModel, mediasModel: this.mediasModel };
      localStorage.setItem('forms', JSON.stringify(storedForms, null, 2));
    },
    updateCurrentFormInLocalStorage() {
      let storedForms = JSON.parse(localStorage.getItem('forms') || '{}');
      storedForms[this.displayedForm.id] = { formModel: this.formModel, mediasModel: this.mediasModel };
      //localStorage.setItem('forms', JSON.stringify(storedForms, null, 2));
    },
    setDefaultValue(formModel, cid) {
      if (!formModel[cid]) {
        formModel[cid] = this.savedFormModel[cid] ?? '';
      }
    },
    setInitialFormValues(formModel) {
      if (this.displayedForm && this.displayedForm.formbuilderdata && this.displayedForm.formbuilderdata.fields) {
        if (Array.isArray(this.displayedForm.formbuilderdata.fields)) {
          this.displayedForm.formbuilderdata.fields.forEach((field) => {
            if (field.cid && field.field_type && field.field_options) {
              const reportValue = this.displayedForm.allowEdit || !this.displayedForm.allowMultipleSubmit ? this.getFormReportValue(field.cid) : false;
              switch (field.field_type) {
                case 'checkboxes': {
                  let initialValue = {};
                  field.field_options.options.forEach((checkbox) => {
                    //console.debug(`setInitialFormValues checkbox ${checkbox.label}=${checkbox.checked}`);
                    initialValue[checkbox.label] = checkbox.checked;
                  });
                  if (field.field_options.include_other_option) {
                    initialValue['Anderes'] = false;
                  }
                  if (this.savedFormModel[field.cid] && field.field_options.options.length === Object.keys(initialValue).length) {
                    initialValue = this.savedFormModel[field.cid];
                  }
                  if (reportValue) {
                    for (const label in initialValue) {
                      initialValue[label] = label === reportValue;
                    }
                  }
                  formModel[field.cid] = initialValue;
                  //console.debug(`Checkbox formModel of field reportValue=${JSON.stringify(reportValue, null, 2)} ${field.cid}=${JSON.stringify(initialValue)}`);
                  break;
                }
                case 'radio': {
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else {
                    field.field_options.options.forEach((item) => {
                      if (item.checked) {
                        formModel[field.cid] = item.label;
                      }
                    });
                  }
                  if (this.savedFormModel[field.cid]) {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                }
                case 'table':
                  formModel[field.cid] = [];
                  if (Array.isArray(reportValue)) {
                    const labels = field.field_options.columns.map((c) => c.label);
                    const chunks = [];
                    while (reportValue.length > 0) {
                      chunks.push(reportValue.splice(0, field.field_options.columns.length));
                    }
                    for (let chunk of chunks) {
                      let colData = {};
                      chunk.forEach((val, idx) => {
                        colData[labels[idx]] = val;
                      });
                      formModel[field.cid].push(colData);
                    }
                  }
                  break;
                case 'dropdown': {
                  const defaultOption = field.field_options.options.find((o) => o.checked === true);
                  formModel[field.cid] = defaultOption === undefined ? undefined : defaultOption.label;
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  }
                  if (this.savedFormModel[field.cid]) {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                }
                case 'dynamicDropdown': {
                  const defaultOption = field.field_options.options.find((o) => o.checked === true);
                  formModel[field.cid] = defaultOption === undefined ? undefined : defaultOption.label + ' ' + defaultOption.email;
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  }
                  if (this.savedFormModel[field.cid]) {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                }
                case 'text':
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else if (field.field_options.auto_fill) {
                    formModel[field.cid] = this.autofill[field.field_options.auto_fill];
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                case 'paragraph': // This is a textarea
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else if (field.field_options.auto_fill) {
                    formModel[field.cid] = this.autofill[field.field_options.auto_fill];
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                case 'number':
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else if (field.field_options.auto_fill) {
                    formModel[field.cid] = this.autofill[field.field_options.auto_fill];
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                case 'email':
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else if (field.field_options.auto_fill) {
                    formModel[field.cid] = this.autofill[field.field_options.auto_fill];
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                case 'file':
                  this.setDefaultValue(formModel, field.cid);
                  break;
                case 'date':
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else if (field.field_options.current_time) {
                    formModel[field.cid] = new Date().toISOString().substring(0, 10);
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                case 'time': {
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else if (field.field_options.current_time) {
                    formModel[field.cid] = dateUtils.formatTimeHHmm(new Date());
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                }
                case 'address':
                  formModel[field.cid + '_Ort'] = this.getFormReportValue(field.cid + '_Ort');
                  formModel[field.cid + '_PLZ'] = this.getFormReportValue(field.cid + '_PLZ');
                  formModel[field.cid + '_Strasse'] = this.getFormReportValue(field.cid + '_Strasse');
                  if (this.savedFormModel[field.cid + '_Ort']) {
                    this.setDefaultValue(formModel, field.cid + '_Ort');
                  }
                  if (this.savedFormModel[field.cid + '_PLZ']) {
                    this.setDefaultValue(formModel, field.cid + '_PLZ');
                  }
                  if (this.savedFormModel[field.cid + '_Strasse']) {
                    this.setDefaultValue(formModel, field.cid + '_Strasse');
                  }
                  break;
                case 'range':
                  formModel[field.cid + '_from'] = this.getFormReportValue(field.cid + '_from');
                  formModel[field.cid + '_to'] = this.getFormReportValue(field.cid + '_to');
                  formModel[field.cid] = '';

                  if (this.savedFormModel[field.cid + '_from']) {
                    this.setDefaultValue(formModel, field.cid + '_from');
                  }
                  if (this.savedFormModel[field.cid + '_to']) {
                    this.setDefaultValue(formModel, field.cid + '_to');
                  }
                  if (this.savedFormModel[field.cid]) {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                case 'qrcode':
                  if (reportValue) {
                    formModel[field.cid] = reportValue;
                  } else {
                    this.setDefaultValue(formModel, field.cid);
                  }
                  break;
                default:
                  if (!['section_break', 'static_link', 'image'].includes(field.field_type)) {
                    formModel[field.cid] = reportValue;
                  }
              }
            }
          });
        }
      }
      return formModel;
    },
    getFormReportValue(fieldCid) {
      if (this.formId && this.formReports[this.formId]) {
        if (this.formReports[this.formId].data && Array.isArray(this.formReports[this.formId].data)) {
          const reportValue = this.formReports[this.formId].data.filter((item) => item.name === fieldCid);
          if (reportValue.length == 1) {
            return reportValue[0].value;
          }
          if (reportValue.length > 1) {
            const reportValues = [];
            reportValue.forEach((item) => {
              reportValues.push(item.value);
            });
            return reportValues;
          }
        }
      }
      return '';
    },
    setInitialMediaValues(mediasModel) {
      // set report values
      if (
        this.displayedForm &&
        this.displayedForm.allowEdit &&
        this.displayedForm.allowEdit === 0 &&
        this.displayedForm.allowMultipleSubmit &&
        this.displayedForm.allowMultipleSubmit === 0
      ) {
        if (this.formReports[this.formId] && this.formReports[this.formId].medias) {
          this.formReports[this.formId].medias.forEach(async (media) => {
            const r = await api.getReportImage(media.filename, media.checksum);
            const base64File = await preprocessImage(r.data);
            mediasModel.push({
              field: media.cid,
              base64File,
            });
          });
        }
      }
      return mediasModel;
    },
    resetForm() {
      console.debug('resetForm');
      this.removeFormInLocalStorage();
      this.savedFormModel = {};
      this.formModel = {};
      this.mediasModel = [];
      this.formModel = this.setInitialFormValues(this.formModel);
      console.debug(`formModel= ${JSON.stringify(this.formModel, null, 2)}`);
      this.mediasModel = this.setInitialMediaValues(this.mediasModel);
      this.updateCurrentFormInLocalStorage();
      // this.fillFormFromLocalStorage();
    },
    async sendForm(data) {
      const instance = this.$route.params.instance;
      const moduleConfig = this.getModuleConfig('Form', instance);

      console.debug('send form', JSON.stringify(data.formData));

      this.disableSendButton = true;
      const payload = {
        instance: this.$route.params.instance,
        module: moduleConfig.title,
        data: data?.formData,
        medias: data?.mediaData,
        formId: this.$route.params.formId,
      };
      try {
        await this.sendFormAction(payload);

        this.removeFormInLocalStorage();

        this.disableSendButton = false;

        if (this.displayedForm.allowMultipleSubmit === 0) {
          this.displayedForm.submitted = 1;
          if (data && data.formData) {
            this.formReports[this.$route.params.formId] = {
              data: data?.formData,
              medias: data?.mediaData,
            };
          }
          if (this.displayedForm.allowMultipleSubmit === 1) {
            //Reset form after send of multiple submittable form that user doesn't send it twice
            this.resetForm();
          }
        } else {
          this.$router.push('/forms/' + this.$route.params.instance);
        }
      } catch (error) {
        this.disableSendButton = false;
        return;
      }
      this.mx_isLeftDrawerOpen = true;
    },
    removeFormInLocalStorage() {
      console.debug(`removeFormInLocalStorage of current form with id=${this.formId}`);
      let storedForms = JSON.parse(localStorage.getItem('forms') || '{}');
      delete storedForms[this.formId];
      localStorage.setItem('forms', JSON.stringify(storedForms, null, 2));
    },
    /**
     * Check if the form is already in the local storage and set the formModel and mediasModel
     * @param formId
     * @returns {boolean}
     */
    checkIfIdIsInLocalStorage(formId) {
      let forms = JSON.parse(localStorage.getItem('forms') || '{}');
      let thisForm = forms[formId];
      if (thisForm) {
        let { formModel, mediasModel } = thisForm;
        this.formModel = formModel;
        this.mediasModel = mediasModel;
        return true;
      }
      return false;
    },
    loadInitialFormValues() {
      if (this.autofill && Object.keys(this.autofill).length > 0) {
        this.formModel = {};
        this.mediasModel = [];
        this.formModel = this.setInitialFormValues(this.formModel);
      }
    },
  },
  watch: {
    // dataLoaded() {
    //   if (this.formId) {
    //     this.resetForm();
    //   }
    // },
    formId(newValue, oldValue) {
      console.debug(`formId changed from ${oldValue} to ${newValue}`);

      if (newValue && !this.checkIfIdIsInLocalStorage(newValue)) {
        this.resetForm();
      } else if (newValue) {
        this.fillFormFromLocalStorage();
        this.loadInitialFormValues();
      }
    },
    displayedForm(v) {
      this.loadInitialFormValues();
    },
  },
};
</script>
